import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import "./searchableDropdownField.css";

const SearchableDropdown = ({
  options = [],
  label,
  id,
  selectedVal,
  handleChange,
  placeholder,
  onFocus,
  disabled,
  notFoundContent,
  loading // Loading prop to indicate whether data is being fetched
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery("");
        setHighlightedIndex(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    if (!isOpen && onFocus) {
      onFocus()
    }
    if (!isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 320);
    }
    setIsOpen((prev) => !prev);
  };

  const selectOption = (option) => {
    handleChange(option[id]);
    setIsOpen(false);
    setQuery("");
    setHighlightedIndex(null);
  };

  const getDisplayValue = () => {
    if (selectedVal) {
      const selectedOption = options.find((option) => option[id] === selectedVal);
      return selectedOption ? selectedOption[label] : "";
    }
    return "";
  };

  const filterOptions = () => {
    return options.filter(
      (option) => option[label]?.toLowerCase().includes(query.toLowerCase())
    );
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
  };

  const handleKeyDown = (e) => {
    const optionsList = filterOptions();
    const numberOfOptions = optionsList.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (highlightedIndex === null || highlightedIndex === numberOfOptions - 1) {
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex(highlightedIndex + 1);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (highlightedIndex === null || highlightedIndex === 0) {
        setHighlightedIndex(numberOfOptions - 1);
      } else {
        setHighlightedIndex(highlightedIndex - 1);
      }
    } else if (e.key === "Enter" && highlightedIndex !== null) {
      selectOption(filterOptions()[highlightedIndex]);
    }
  };

  const filteredOptions = filterOptions();
  const hasNoMatches = query && filteredOptions.length === 0;

  return (
    <div className={`dropdown ${disabled ? "disabled-dropdown" : ""} ${isOpen ? "open" : ""}`} ref={inputRef}>
      <div className="control" onClick={toggleDropdown}>
        <div className="selected-value">
          <input
            type="text"
            value={getDisplayValue()}
            placeholder={placeholder || "Select"}
            readOnly
            disabled={disabled}
          />
        </div>
        <div className={`arrow ${isOpen ? "open" : ""}`}></div>
      </div>

      <div className={`options ${isOpen ? "open" : "close"} ${openUpward ? "open-upward" : ""}`}>
        <div className="search-container">
          <Search className="search-icon" size={16} />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder="Find roles"
            value={query}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Options list with loading state */}
        <div className="options-list">
          {loading ? (
            <div className="loading-options">
              <div className="options-spinner"></div>
              <span>Loading roles...</span>
            </div>
          ) : hasNoMatches ? (
            <div className="no-options">{notFoundContent || "No matching options"}</div>
          ) : filteredOptions.length === 0 ? (
            <div className="no-options">No roles available</div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                onClick={() => selectOption(option)}
                className={`option ${option[id] === selectedVal ? "selected" : ""} ${highlightedIndex === index ? "highlighted" : ""}`}
                key={`${id}-${index}`}
              >
                {option[label]}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchableDropdown;